/* ============================================================
   custom-cart.js
   Load this globally (defer) — e.g. in theme.liquid before </body>,
   AFTER the cart-drawer snippet has been rendered.
   Handles:
     1. Variant swatch selection -> updates hidden variant id, price, availability
     2. AJAX Add to Cart (/cart/add.js) -> opens drawer, refreshes drawer content
     3. Buy Now -> adds to cart then redirects straight to /checkout
     4. Cart drawer open/close + qty update / remove (AJAX /cart/change.js)
   No external dependencies, no build step.
   ============================================================ */

(function () {
  'use strict';

  /* ---------------------------------------------------------
     Small helpers
  --------------------------------------------------------- */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function formatMoney(cents, format) {
    // Minimal fallback formatter; Shopify's own money filter already
    // formats amounts rendered by Liquid. This is only used for the
    // live price preview when a variant changes on the client side.
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, format);
    }
    return '₹' + (cents / 100).toFixed(2);
  }

  /* ===========================================================
     1. PRODUCT FORM + VARIANT SWATCHES
  =========================================================== */
  function initProductForms() {
    qsa('[data-product-form]').forEach((wrapper) => {
      const root = qs('product-form-swatches', wrapper);
      if (!root || root.dataset.pfsInit) return;
      root.dataset.pfsInit = 'true';

      const form = qs('[data-product-form-el]', root);
      const variantInput = qs('[data-variant-id-input]', form);
      const variantsJsonEl = qs('[data-product-json]', wrapper);
      const variants = JSON.parse(variantsJsonEl.textContent);

      const priceEl = qs('[data-product-price]', root);
      const comparePriceEl = qs('[data-product-compare-price]', root);
      const availabilityEl = qs('[data-availability]', root);
      const addBtn = qs('[data-add-to-cart]', root);
      const buyBtn = qs('[data-buy-now]', root);
      const errorEl = qs('[data-form-error]', root);
      const qtyInput = qs('[data-qty-input]', root);

      /* ---- current selections, keyed by option position (1-based) ---- */
      const optionGroups = qsa('[data-option-position]', root).filter(
        (el) => el.classList.contains('pfs-option')
      );
      const selections = {};
      optionGroups.forEach((group) => {
        const pos = group.dataset.optionPosition;
        const active = qs('[data-active="true"]', group);
        if (active) selections[pos] = active.dataset.value;
      });

      function findMatchingVariant() {
        return variants.find((v) => {
          const opts = [v.option1, v.option2, v.option3];
          return Object.keys(selections).every((pos) => {
            return opts[Number(pos) - 1] === selections[pos];
          });
        });
      }

      function setUnavailableSwatches(matchedVariant) {
        // Grey out combinations that would lead to a non-existent/unavailable variant.
        optionGroups.forEach((group) => {
          const pos = group.dataset.optionPosition;
          qsa('[data-swatch]', group).forEach((swatchBtn) => {
            const testValue = swatchBtn.dataset.value;
            const testSelections = Object.assign({}, selections, { [pos]: testValue });
            const testVariant = variants.find((v) => {
              const opts = [v.option1, v.option2, v.option3];
              return Object.keys(testSelections).every(
                (p) => opts[Number(p) - 1] === testSelections[p]
              );
            });
            const unavailable = !testVariant || !testVariant.available;
            swatchBtn.classList.toggle('pfs-swatch--unavailable', unavailable);
          });
        });
      }

      function updateUI() {
        const variant = findMatchingVariant();
        if (!variant) {
          availabilityEl.innerHTML = '<span class="pfs-dot pfs-dot--out"></span> Unavailable';
          addBtn.setAttribute('disabled', 'disabled');
          buyBtn.setAttribute('disabled', 'disabled');
          return;
        }

        variantInput.value = variant.id;
        priceEl.textContent = formatMoney(variant.price);

        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          if (comparePriceEl) {
            comparePriceEl.textContent = formatMoney(variant.compare_at_price);
            comparePriceEl.hidden = false;
          }
        } else if (comparePriceEl) {
          comparePriceEl.hidden = true;
        }

        if (variant.available) {
          availabilityEl.innerHTML = '<span class="pfs-dot pfs-dot--in"></span> In stock';
          addBtn.removeAttribute('disabled');
          buyBtn.removeAttribute('disabled');
          addBtn.querySelector('[data-btn-text]').textContent = 'Add to cart';
        } else {
          availabilityEl.innerHTML = '<span class="pfs-dot pfs-dot--out"></span> Sold out';
          addBtn.setAttribute('disabled', 'disabled');
          buyBtn.setAttribute('disabled', 'disabled');
          addBtn.querySelector('[data-btn-text]').textContent = 'Sold out';
        }

        // update URL (without reload) so refresh/share keeps the variant
        if (window.history && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }

        setUnavailableSwatches(variant);
      }

      /* ---- swatch click handling ---- */
      qsa('[data-swatch]', root).forEach((btn) => {
        btn.addEventListener('click', () => {
          const group = btn.closest('.pfs-option');
          const pos = group.dataset.optionPosition;

          qsa('[data-swatch]', group).forEach((b) => {
            b.dataset.active = 'false';
            b.setAttribute('aria-checked', 'false');
          });
          btn.dataset.active = 'true';
          btn.setAttribute('aria-checked', 'true');

          selections[pos] = btn.dataset.value;
          const label = qs('[data-selected-value-label]', group);
          if (label) label.textContent = btn.dataset.value;

          updateUI();
        });
      });

      /* ---- quantity stepper ---- */
      qs('[data-qty-decrease]', root)?.addEventListener('click', () => {
        qtyInput.value = Math.max(1, parseInt(qtyInput.value || '1', 10) - 1);
      });
      qs('[data-qty-increase]', root)?.addEventListener('click', () => {
        qtyInput.value = parseInt(qtyInput.value || '1', 10) + 1;
      });

      /* ---- helpers to toggle button loading state ---- */
      function setLoading(btn, isLoading) {
        const spinner = qs('[data-btn-spinner]', btn);
        const text = qs('[data-btn-text]', btn);
        if (isLoading) {
          btn.setAttribute('disabled', 'disabled');
          spinner.hidden = false;
          if (text) text.style.opacity = '0.4';
        } else {
          btn.removeAttribute('disabled');
          spinner.hidden = true;
          if (text) text.style.opacity = '1';
        }
      }

      function showError(message) {
        errorEl.textContent = message;
        errorEl.hidden = false;
        setTimeout(() => { errorEl.hidden = true; }, 4000);
      }

      /* ---- ADD TO CART (AJAX) ---- */
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        setLoading(addBtn, true);

        const body = {
          id: variantInput.value,
          quantity: parseInt(qtyInput.value || '1', 10),
        };

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              throw new Error(data.description || 'Could not add item to cart');
            }
            setLoading(addBtn, false);
            window.CartDrawer.refresh().then(() => window.CartDrawer.open());
          })
          .catch((err) => {
            setLoading(addBtn, false);
            showError(err.message);
          });
      });

      /* ---- BUY NOW: add to cart, then go straight to checkout ---- */
      buyBtn.addEventListener('click', function () {
        setLoading(buyBtn, true);

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: variantInput.value,
            quantity: parseInt(qtyInput.value || '1', 10),
          }),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) throw new Error(data.description || 'Could not add item to cart');
            window.location.href = '/checkout';
          })
          .catch((err) => {
            setLoading(buyBtn, false);
            showError(err.message);
          });
      });

      updateUI();
    });
  }

  /* ===========================================================
     2. CART DRAWER
  =========================================================== */
  const CartDrawer = {
    el: null,
    init() {
      this.el = qs('[data-cart-drawer]');
      if (!this.el) return;

      qs('[data-cart-drawer-overlay]', this.el).addEventListener('click', () => this.close());
      qs('[data-cart-drawer-close]', this.el).addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      // Any element on the page with data-open-cart-drawer opens it (e.g. header cart icon)
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-open-cart-drawer]');
        if (opener) {
          e.preventDefault();
          this.refresh().then(() => this.open());
        }
      });

      this.bindBodyEvents();
    },

    open() {
      this.el.dataset.open = 'true';
      this.el.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
    },

    close() {
      this.el.dataset.open = 'false';
      this.el.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
    },

    /* Re-fetch /cart.js and re-render the drawer body + footer + count badges */
    refresh() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((res) => res.json())
        .then((cart) => {
          this.render(cart);
          return cart;
        });
    },

    render(cart) {
      const body = qs('[data-cart-drawer-body]', this.el);
      const footer = qs('[data-cart-drawer-footer]', this.el);
      const countEls = qsa('[data-cart-count]');
      countEls.forEach((el) => (el.textContent = '(' + cart.item_count + ')'));

      // also update any header cart bubble outside the drawer
      qsa('[data-cart-badge]').forEach((el) => (el.textContent = cart.item_count));

      if (cart.item_count === 0) {
        body.innerHTML =
          '<div class="cart-drawer__empty"><p>Your cart is empty.</p>' +
          '<a href="/collections/all" class="pfs-btn pfs-btn--add">Continue shopping</a></div>';
        footer.hidden = true;
        return;
      }

      footer.hidden = false;
      qs('[data-cart-subtotal]', footer).textContent = formatMoney(cart.total_price);

      const itemsHtml = cart.items
        .map((item) => {
          const img = item.image
            ? '<img src="' + item.image.replace(/(\.[a-zA-Z0-9]+)(\?.*)?$/, '_160x$1') + '" alt="" width="64" height="64" loading="lazy">'
            : '';
          const variantLine = item.variant_title
            ? '<p class="cart-drawer__item-variant">' + item.variant_title + '</p>'
            : '';
          return (
            '<li class="cart-drawer__item" data-cart-item data-key="' + item.key + '">' +
              '<a href="' + item.url + '" class="cart-drawer__item-image">' + img + '</a>' +
              '<div class="cart-drawer__item-info">' +
                '<a href="' + item.url + '" class="cart-drawer__item-title">' + item.product_title + '</a>' +
                variantLine +
                '<div class="cart-drawer__item-qty">' +
                  '<button type="button" class="pfs-qty__btn" data-cart-qty-decrease data-key="' + item.key + '">−</button>' +
                  '<input type="number" min="0" value="' + item.quantity + '" class="pfs-qty__input" data-cart-qty-input data-key="' + item.key + '">' +
                  '<button type="button" class="pfs-qty__btn" data-cart-qty-increase data-key="' + item.key + '">+</button>' +
                '</div>' +
              '</div>' +
              '<div class="cart-drawer__item-price">' + formatMoney(item.final_line_price) + '</div>' +
              '<button type="button" class="cart-drawer__item-remove" data-cart-remove data-key="' + item.key + '" aria-label="Remove item">&times;</button>' +
            '</li>'
          );
        })
        .join('');

      body.innerHTML = '<ul class="cart-drawer__items" data-cart-items>' + itemsHtml + '</ul>';
    },

    changeLine(key, quantity) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity }),
      })
        .then((res) => res.json())
        .then((cart) => this.render(cart));
    },

    /* Delegate qty +/- , qty input change, and remove clicks inside the drawer body */
    bindBodyEvents() {
      const body = qs('[data-cart-drawer-body]', this.el);

      body.addEventListener('click', (e) => {
        const decBtn = e.target.closest('[data-cart-qty-decrease]');
        const incBtn = e.target.closest('[data-cart-qty-increase]');
        const removeBtn = e.target.closest('[data-cart-remove]');

        if (decBtn) {
          const input = qs('[data-cart-qty-input][data-key="' + decBtn.dataset.key + '"]', body);
          const next = Math.max(0, parseInt(input.value, 10) - 1);
          this.changeLine(decBtn.dataset.key, next);
        }
        if (incBtn) {
          const input = qs('[data-cart-qty-input][data-key="' + incBtn.dataset.key + '"]', body);
          const next = parseInt(input.value, 10) + 1;
          this.changeLine(incBtn.dataset.key, next);
        }
        if (removeBtn) {
          this.changeLine(removeBtn.dataset.key, 0);
        }
      });

      body.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-qty-input]');
        if (input) {
          const val = Math.max(0, parseInt(input.value, 10) || 0);
          this.changeLine(input.dataset.key, val);
        }
      });
    },
  };

  window.CartDrawer = CartDrawer;

  document.addEventListener('DOMContentLoaded', function () {
    CartDrawer.init();
    initProductForms();
  });
})();
